import { DriverOfferBox } from "@/components/price/PriceOffers";
import React from "react";
import { createPortal } from "react-dom";
import { CalendarClock, MapPin, Users, Loader2, CreditCard, Banknote, X, Navigation } from "lucide-react";
import { fmtSched } from "@/components/price/ScheduledRidesSheet";

const dayTitle = (iso) => {
  const d = new Date(iso);
  const t = new Date();
  const tm = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1);
  if (d.toDateString() === t.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === tm.toDateString()) return "Demain";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
};

export default function DriverScheduledSheet({ data, tab, setTab, busyId, onAccept, onWithdraw, onReload, onClose }) {
  const list = tab === "open" ? data.open : data.mine;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="driver-scheduled-sheet">
      <div className="w-full sm:max-w-md max-h-[90dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#FFF8F1] p-5 street-border street-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-[Outfit] text-[18px] font-black"><CalendarClock className="h-5 w-5 text-primary" /> Courses programmées</div>
          <button type="button" onClick={onClose} data-testid="driver-scheduled-close" className="grid h-9 w-9 place-items-center rounded-xl bg-white street-border-thin" aria-label="Fermer"><X className="h-4 w-4" strokeWidth={3} /></button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[["open", `À prendre (${data.open.length})`], ["mine", `Mes courses (${data.mine.length})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`driver-scheduled-tab-${k}`}
              className={`rounded-xl py-2.5 text-[13px] font-black street-border-thin transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "bg-white"}`}>{l}</button>
          ))}
        </div>
        {!data.has_zone && tab === "open" && (
          <p className="mt-3 rounded-xl bg-[#FFF3CC] p-3 text-[12px] font-semibold street-border-thin" data-testid="driver-scheduled-no-zone">
            Passe « En ligne » une fois pour qu&apos;on connaisse ta zone : tu verras alors les courses programmées autour de toi.
          </p>
        )}
        <DayGroups
          list={list}
          mine={tab === "mine"}
          busyId={busyId}
          onAction={tab === "open" ? onAccept : onWithdraw}
          onReload={onReload}
          empty={tab === "open"
            ? "Aucune course programmée dans ta zone pour l'instant. Tu recevras une notification dès qu'un passager en programme une."
            : "Tu n'as pas encore accepté de course programmée."}
        />
      </div>
    </div>,
    document.body,
  );
}

function DayGroups({ list, mine, busyId, onAction, onReload, empty }) {
  if (!list.length) return <p className="mt-6 text-center text-[13px] font-semibold text-foreground/60" data-testid="driver-scheduled-empty">{empty}</p>;
  const groups = [];
  list.forEach((r) => {
    const k = new Date(r.scheduled_at).toDateString();
    const g = groups.find((x) => x.k === k);
    if (g) g.items.push(r); else groups.push({ k, iso: r.scheduled_at, items: [r] });
  });
  return groups.map((g) => (
    <div key={g.k} className="mt-4">
      <h3 className="font-[Outfit] text-[13px] font-black uppercase tracking-widest text-foreground/60">{dayTitle(g.iso)}</h3>
      {g.items.map((r) => <SchedCard key={r.id} r={r} busy={busyId === r.id} mine={mine} onAction={() => onAction(r)} onReload={onReload} />)}
    </div>
  ));
}

function SchedCard({ r, busy, mine, onAction, onReload }) {
  const awaiting = r.status === "scheduled_awaiting_payment";
  return (
    <div className="mt-2 rounded-3xl bg-white p-4 street-border street-shadow-sm" data-testid={`driver-sched-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="rounded-xl bg-accent px-2.5 py-1 font-[Outfit] text-[13px] font-black capitalize street-border-thin">{fmtSched(r.scheduled_at)}</div>
        <div className="text-right">
          <div className="font-[Outfit] text-[20px] font-black tabular-nums" data-testid={`driver-sched-price-${r.id}`}>{Number(!mine && r.negotiable && r.passenger_proposed_eur ? r.passenger_proposed_eur : r.price_eur).toFixed(2)}€</div>
          {!mine && r.negotiable && <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50">{r.passenger_proposed_eur ? "Prix du passager" : "Prix recommandé"}</div>}
        </div>
      </div>
      <div className="mt-2 space-y-1 text-[12px] font-semibold text-foreground/75">
        <div className="flex gap-1.5"><MapPin className="mt-0.5 h-3.5 w-3.5 flex-none text-primary" />{r.from_address}</div>
        <div className="flex gap-1.5"><MapPin className="mt-0.5 h-3.5 w-3.5 flex-none text-accent-foreground" />{r.to_address}</div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-black">
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/50 px-2 py-0.5"><Users className="h-3 w-3" />{r.seats}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/50 px-2 py-0.5">
          {r.payment_method === "card" ? <CreditCard className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}
          {r.payment_method === "card" ? "Carte" : "Espèces"}
        </span>
        <span className="rounded-full bg-secondary/50 px-2 py-0.5">{r.client_name}</span>
        {r.pickup_distance_km != null && (
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary/50 px-2 py-0.5" data-testid={`driver-sched-distance-${r.id}`}><Navigation className="h-3 w-3" />à {r.pickup_distance_km} km</span>
        )}
        {mine && (
          <span className={`rounded-full px-2 py-0.5 ${awaiting ? "bg-[#FFF3CC]" : "bg-[#D6F5D6]"}`} data-testid={`driver-sched-status-${r.id}`}>
            {awaiting ? "Paiement passager en attente" : "Confirmée"}
          </span>
        )}
      </div>
      {!mine && r.negotiable ? <DriverOfferBox req={r} onSent={onReload} /> : <button
        type="button"
        disabled={busy}
        onClick={onAction}
        data-testid={`driver-sched-${mine ? "withdraw" : "accept"}-${r.id}`}
        className={`street-press mt-3 flex w-full items-center justify-center gap-1.5 rounded-2xl py-3 font-[Outfit] text-[14px] font-black street-border-thin disabled:opacity-60 ${mine ? "bg-white" : "bg-primary text-primary-foreground"}`}
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {mine ? "Me désister" : "Accepter la course"}
      </button>}
    </div>
  );
}
